import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence, validateFormat } from 'ember-changeset-validations/validators';
import { buildSummaryAttributeChangesets, type SummaryAttributeChangesets } from 'garaje/changesets/summary-attribute';
import type Device from 'garaje/models/device';
import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type SummaryPageModel from 'garaje/models/summary-page';
import type TransitionConfirmService from 'garaje/services/transition-confirm';
import { routeEvent } from 'garaje/utils/decorators/route';
import type { RecordArray } from 'garaje/utils/type-utils';
import validateConditional from 'garaje/validators/conditional';
import { hash } from 'rsvp';

import type { VisitorsSettingsRouteModel } from '../../../route';

import type VisitorsSettingsVisitorTypesFlowFinalScreenController from './controller';

function buildValidations() {
  return {
    videoUrl: validateConditional({
      if(_key: string, _newValue: unknown, _oldValue: unknown, changes: unknown, content: object): boolean {
        const model = content as SummaryPageModel;
        const kind = (changes as Record<string, unknown>)['kind'] as string | undefined;
        return (kind || model.kind) === 'video';
      },
      then: validateFormat({
        type: 'url',
        allowBlank: false,
      }),
      else(): boolean {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
    message: validateConditional({
      if(_key: string, _newValue: unknown, _oldValue: unknown, changes: unknown, content: object): boolean {
        const model = content as SummaryPageModel;
        const kind = (changes as Record<string, unknown>)['kind'] as string | undefined;
        return (kind || model.kind) === 'message';
      },
      then: validatePresence(true),
      else(): boolean {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
    image: validateConditional({
      if(_key: string, _newValue: unknown, _oldValue: unknown, changes: unknown, content: object): boolean {
        const model = content as SummaryPageModel;
        const kind = (changes as Record<string, unknown>)['kind'] as string | undefined;
        return (kind || model.kind) === 'image';
      },
      then: validatePresence(true),
      else(): boolean {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
  };
}

export interface FinalScreenRouteModel {
  currentLocation: LocationModel;
  changeset: DetailedChangeset<SummaryPageModel>;
  summaryPage: SummaryPageModel;
  flow: FlowModel;
  ipads?: RecordArray<Device>;
  summaryAttributeChangesets: SummaryAttributeChangesets;
}

export default class VisitorsSettingsVisitorTypesFlowFinalScreenRoute extends Route {
  @service declare transitionConfirm: TransitionConfirmService;

  async model(): Promise<FinalScreenRouteModel> {
    const { currentLocation, flow } = this.modelFor('visitors.settings.visitor-types.flow') as {
      currentLocation: LocationModel;
      flow: FlowModel;
    };
    const { ipads } = <VisitorsSettingsRouteModel>this.modelFor('visitors.settings');

    const { isFulfilled } = flow.summaryPage;
    const summaryPage = await flow.summaryPage;

    // Reload causes loading state which forces FinalScreen component constructor to (re)run
    if (isFulfilled) await summaryPage.reload();

    const changeset = this._buildChangeset(summaryPage);
    const summaryAttributeChangesets = buildSummaryAttributeChangesets(summaryPage);

    return hash({
      currentLocation,
      changeset,
      summaryPage,
      flow,
      ipads,
      summaryAttributeChangesets,
    });
  }

  resetController(controller: VisitorsSettingsVisitorTypesFlowFinalScreenController, isExiting: boolean): void {
    if (isExiting) {
      controller.isEditingCustomFinalMessage = false;
    }
  }

  _buildChangeset(summaryPage: SummaryPageModel): DetailedChangeset<SummaryPageModel> {
    const validations = buildValidations();
    const validator = lookupValidator(validations);

    return Changeset(summaryPage, validator, validations);
  }

  @routeEvent
  routeWillChange(transition: Transition): void {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const model = this.controller.model as FinalScreenRouteModel;
    // Guard the summary page and every per-state tab changeset, so edits on any
    // tab prompt the unsaved-changes confirmation.
    const changesets = [model.changeset, ...Object.values(model.summaryAttributeChangesets)];
    const confirmTask = this.transitionConfirm.displayConfirmTask;

    if (changesets.some((changeset) => changeset.isDirty)) {
      void confirmTask.perform(transition, {
        continue() {
          changesets.forEach((changeset) => changeset.rollback());
        },
      });
    }
  }
}
